import type { ClassValue } from 'clsx';

import { clsxm } from '@vs-shop/utils';
import { Home } from 'lucide-react';
import { Fragment } from 'react';

import { Url } from '../../../route/router';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../utils/intl';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@vs-shop/ui/breadcrumb';

import BreadcrumbMetadata from './BreadcrumbMetadata';

export type BreadcrumbItemData = {
  href: string;
  name: string;
};

type Props = {
  breadcrumbs: BreadcrumbItemData[];
  currentName: string;
  className?: ClassValue;
};

/**
 * rcprofi's trail, composed on `@vs-shop/ui/breadcrumb`.
 *
 * The primitive is the anatomy; what stays here is everything that is this
 * tenant's — the home route, the structured data crawlers read, and the word
 * assistive technology announces the landmark by.
 */
export default function Breadcrumbs({ breadcrumbs, currentName, className }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  return (
    <>
      <BreadcrumbMetadata breadcrumbs={breadcrumbs} currentName={currentName} />

      <Breadcrumb
        aria-label={intl.formatMessage({
          id: 'component.shadcn.ui.breadcrumb.ariaLabel',
          defaultMessage: 'breadcrumb',
        })}
      >
        <BreadcrumbList className={clsxm(className)}>
          <BreadcrumbItem>
            <BreadcrumbLink
              href={Url.home}
              title={intl.formatMessage({
                id: 'breadcrumbs.link.home',
                defaultMessage: 'Domovská stránka',
              })}
            >
              <Home className="size-4 shrink-0 text-foreground-muted hover:text-foreground" />
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          {breadcrumbs.map((breadcrumb) => (
            <Fragment key={breadcrumb.href}>
              <BreadcrumbItem>
                <BreadcrumbLink href={breadcrumb.href} title={breadcrumb.name}>
                  {breadcrumb.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          ))}

          <BreadcrumbItem>
            <BreadcrumbPage>{currentName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
}
